/**
 * GetShouldSendSubscriptionRequestUseCase
 *
 * Determines whether tapping the subscribe CTA on a creator should send a
 * *subscription request* (approval-required flow) instead of subscribing
 * directly.
 *
 * Returns `false` immediately for `active` and `requested` statuses — those
 * states never trigger a new subscription request, so we skip the data-layer
 * read entirely.
 *
 * For `inactive` and `cancelled` statuses, the use case consults the data
 * layer (which reads `users/{id}/userSettings/approve_subscribers` and
 * returns `true` only when `value === 1`).
 *
 * Mirrors Flutter's `_getShouldSendSubscriptionRequest` gating in
 * `lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart`.
 */
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import {
  ExclusiveContentSubscriptionActionsService,
  exclusiveContentSubscriptionActionsService,
} from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscriptionActionsService';

export class GetShouldSendSubscriptionRequestUseCase {
  private service: ExclusiveContentSubscriptionActionsService;

  constructor(service?: ExclusiveContentSubscriptionActionsService) {
    this.service = service ?? exclusiveContentSubscriptionActionsService;
  }

  async execute(
    targetUserId: string,
    currentStatus: ExclusiveContentSubscriptionStatus,
  ): Promise<boolean> {
    if (
      currentStatus === ExclusiveContentSubscriptionStatus.active ||
      currentStatus === ExclusiveContentSubscriptionStatus.requested
    ) {
      return false;
    }
    return this.service.shouldSendSubscriptionRequest(targetUserId);
  }
}

export const createGetShouldSendSubscriptionRequestUseCase = (): GetShouldSendSubscriptionRequestUseCase => {
  return new GetShouldSendSubscriptionRequestUseCase();
};
